"use client";
import { FC } from "react";
import Image from "next/image";
import cn from "classnames";
import { Card, Media as MediaType } from "@/types/General";
import { Calendar, Location } from "@/icons";
import { imageLoader } from "@/helpers";
import WistiaPlayer from "@/components/WistiaPlayer";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import Link from "@/components/Link";
import { Button } from "../../Button";

const DAYS_IN_MILISECONDS = 1000 * 60 * 60 * 24;

const formatDate = (
  startDateString: string | null,
  endDateString: string | null,
) => {
  if (!startDateString) {
    return "";
  }

  const startDate = new Date(startDateString);
  if (endDateString) {
    const endDate = new Date(endDateString);

    // index pages are not translated yet, therefore using en-US formatting for now
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatRange(startDate, endDate);
  }

  // index pages are not translated yet, therefore using en-US formatting for now
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(startDate);
};

type EventCardProps = {
  type?: string;
  media?: MediaType;
} & Extract<Card, { type: "Event" }>;
export const EventCard: FC<EventCardProps> = (props) => {
  const currentMoment = new Date(Date.now());
  const today = currentMoment;

  const startDate = props.startDate ? new Date(props.startDate) : null;
  const endDate = props.endDate ? new Date(props.endDate) : startDate;

  const startDateDifference = Number(startDate?.valueOf()) - today.valueOf();
  const startDateDifferenceInDays = Math.floor(
    startDateDifference / DAYS_IN_MILISECONDS,
  );

  const doesOccurSoon = props.startDate
    ? startDateDifferenceInDays <= 7 && startDateDifferenceInDays >= 0
    : false;

  const doesOccurToday =
    Number(startDate?.valueOf()) < today.valueOf() &&
    today.valueOf() < Number(endDate?.valueOf());

  const isFeatured = props.isFeatured;

  return (
    <Link
      href={props.link?.url ?? ""}
      {...getDataLinkAttributes(props.link)}
      className={cn(
        "focus-visible:outline-swdc-blue-400 group outline-swdc-silver-200 hover:shadow-card active:shadow-card relative flex break-inside-avoid flex-col gap-y-4 p-4 outline-1 outline-solid focus-visible:outline-2 focus-visible:outline-solid",
        isFeatured ? "bg-background-highlight" : "bg-swdc-white",
      )}
      {...getDataAutomationGroup("link")}
      {...getDataAutomationId(props.title)}
    >
      {((doesOccurSoon && props.fomoText?.upcomingText) ||
        (doesOccurToday && props.fomoText?.todayText)) && (
        <div className="bg-swdc-orange-400 text-swdc-black-600 rounded-corner-subtle absolute top-1 right-1 px-2 py-0.5 font-bold">
          {doesOccurSoon
            ? props.fomoText.upcomingText
            : props.fomoText.todayText}
        </div>
      )}
      {props.categories && Boolean(props.categories.length) && (
        <ul className="flex flex-row flex-wrap gap-2">
          {props.categories?.map((category) => (
            <li
              key={category}
              className="border-swdc-silver-200 text-body-2xs text-body-secondary border-1 border-solid px-2 py-1 font-medium"
              {...getDataAutomationGroup("pill")}
              {...getDataAutomationId(props.title)}
            >
              {category}
            </li>
          ))}
        </ul>
      )}
      <div
        className={cn(
          "text-headline font-bold",
          isFeatured ? "text-subhead-xs sm:text-subhead-xl" : "text-subhead-xs",
        )}
      >
        {props.title}
      </div>
      <div className="flex flex-row flex-wrap-reverse gap-4 md:flex-nowrap">
        <div className="flex shrink grow basis-94 flex-col items-start gap-y-8">
          <div className="flex flex-col gap-y-4">
            <p
              className="text-body-s text-body-primary"
              dangerouslySetInnerHTML={{ __html: props.description }}
            />
            <div className="flex flex-col gap-y-2">
              {!props.hideDate && (
                <div className="flex flex-row gap-x-2">
                  <Calendar className="shrink-0" />
                  <p className="text-body-xs text-body-primary font-medium">
                    {formatDate(props.startDate, props.endDate)}
                  </p>
                </div>
              )}
              {props.location && (
                <div className="flex flex-row gap-x-2">
                  <Location className="shrink-0" />
                  <p className="text-body-xs text-body-primary font-medium">
                    {props.location}
                  </p>
                </div>
              )}
            </div>
          </div>
          {props.link?.url && props.link?.title && (
            <Button
              {...(props.isFeatured
                ? { variant: "secondary" }
                : { variant: "tertiary", icon: "none" })}
              className="group-hover:outline-button-secondary-content-hover group-hover:outline-button-secondary-outline-hover group-active:outline-button-secondary-content-pressed group-active:outline-button-secondary-outline-pressed group-disabled:outline-button-secondary-content-disabled group-disabled:outline-button-secondary-outline-disabled"
              {...getDataAutomationId(props.title)}
            >
              {props.link.title}
            </Button>
          )}
        </div>
        {props.media && props.media.type && (
          <div className="w-full shrink grow basis-97.5">
            {props.media?.type === "image" && (
              <Image
                src={props.media.image}
                loader={imageLoader({
                  image: props.media.image,
                  alt: props.title,
                  width: 782,
                  height: 440,
                })}
                width={782}
                height={440}
                alt={props.title}
              />
            )}
            {props.media?.type === "video" && (
              <WistiaPlayer uuid={props.media.videoId} />
            )}
          </div>
        )}
      </div>
    </Link>
  );
};
